import { useQuery } from "@tanstack/react-query";
import { BarChart3, CircleDot, Code2, LogOut, Plug, ScrollText, Server, ShieldCheck, Package } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const NAV_ITEMS = [
  { to: "/machines", label: "Machines", icon: Server },
  { to: "/releases", label: "Releases", icon: Package },
  { to: "/profiles", label: "Profiles", icon: ShieldCheck },
  { to: "/image-pulls", label: "Image pulls", icon: BarChart3 },
  { to: "/audit", label: "Audit log", icon: ScrollText },
];

export function AppSidebar() {
  const { logout } = useAuth();
  const location = useLocation();
  const { data: subscriptions } = useQuery({
    queryKey: ["subscriptions"],
    queryFn: api.listSubscriptions,
  });

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="flex size-6 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-semibold">
            D
          </div>
          <span className="text-sm font-semibold group-data-[collapsible=icon]:hidden">
            DenkBroker
          </span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Manage</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={location.pathname.startsWith(item.to)}
                    tooltip={item.label}
                  >
                    <NavLink to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel>Services</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {subscriptions?.map((subscription) => {
                const to = `/services/${subscription.slug}`;
                return (
                  <SidebarMenuItem key={subscription.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={location.pathname === to}
                      tooltip={subscription.name}
                    >
                      <NavLink to={to}>
                        <Plug />
                        <span>{subscription.name}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname === "/services"}
                  tooltip="All services"
                >
                  <NavLink to="/services">
                    <Plug />
                    <span>All services</span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="View source on GitHub">
              <a
                href="https://github.com/denkboxgmbh/DenkBroker"
                target="_blank"
                rel="noreferrer"
              >
                <Code2 />
                <span>Source code</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Open a new issue on GitHub">
              <a
                href="https://github.com/denkboxgmbh/DenkBroker/issues/new"
                target="_blank"
                rel="noreferrer"
              >
                <CircleDot />
                <span>Report an issue</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={logout} tooltip="Sign out">
              <LogOut />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
